import { PopUpMessageContext, useApp, usePracticeAccess } from '@hb/react'
import { INVOICES, INVOICES_DELETED } from '@hb/shared'
import { collection, getDocs, limit, query, where } from 'firebase/firestore'
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { useNavigate } from '../../hooks/navigation'
import { db } from '../../backend/db/db'

type InvoiceTabName =
  | 'pending-approval'
  | 'current'
  | 'sent'
  | 'paid'
  | 'settings'
  | 'archived'
  | 'deleted'
  | 'outstanding'
// export const invoiceTabNames: InvoiceTabName[] = [
//   'pending-approval',
//   'current',
//   'sent',
//   'paid',
//   'settings',
//   'archived',
//   'deleted',
//   'outstanding',
// ]
export interface InvoicesViewContextValue {
  selectedPracticeId: string | null
  selectPractice: (id: string | null) => void
  selectedInvoiceId: string | null
  selectInvoice: (id: string | null) => void
  tabIndex: number
  invoicesTab?: InvoiceTabName
  basePath: string
  onTabSelect: (index: number) => void
  goToInvoiceByNumber: (invoiceNumber: string) => Promise<void>
  selectPregnancy: (invoiceId: string, assessmentId: string, tab?: string) => void
  deselectPregnancy: () => void
  createInvoiceOpen: boolean
  setCreateInvoiceOpen: (open: boolean) => void
}

const InvoicesViewContext = createContext<InvoicesViewContextValue>({
  selectedPracticeId: null,
  selectPractice: () => {},
  selectedInvoiceId: null,
  selectInvoice: () => {},
  basePath: '',
  goToInvoiceByNumber: async () => {},
  deselectPregnancy: () => {},
  tabIndex: 0,
  onTabSelect: () => {},
  selectPregnancy: () => {},
  createInvoiceOpen: false,
  setCreateInvoiceOpen: () => {},
})

export const useInvoicesView = () => useContext<InvoicesViewContextValue>(InvoicesViewContext)

const useInvoicesViewData = (basePath: string): InvoicesViewContextValue => {
  const { appName } = useApp()
  const { selectedPracticeId: appPracticeId } = usePracticeAccess()
  const [manualPracticeId, setSelectedPracticeId] = useState<string | null>(null)
  const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false)

  const selectedPracticeId = useMemo(
    () => (appName === 'app' ? manualPracticeId : appPracticeId),
    [appName, appPracticeId, manualPracticeId],
  )

  const { invoiceId: selectedInvoiceId = null, invoicesTab } = useParams<{
    invoiceId?: string
    invoicesTab?: InvoiceTabName
  }>()
  const defaultTab = useMemo(
    () => (appName === 'app' ? 'pending-approval' : 'outstanding'),
    [appName],
  )

  const tabIndex = useMemo(() => {
    switch (appName) {
      case 'app':
        switch (invoicesTab) {
          case 'pending-approval':
            return 0
          case 'current':
            return 1
          case 'sent':
            return 2
          case 'paid':
            return 3
          case 'settings':
            return 4
          case 'archived':
            return 5
          case 'deleted':
            return 6
          default:
            return 0
        }
      case 'providers-app':
        switch (invoicesTab) {
          case 'outstanding':
            return 0
          case 'paid':
            return 1
          default:
            return 0
        }
      default:
        return 0
    }
  }, [invoicesTab, appName])
  const navigate = useNavigate()
  const onTabSelect = useCallback(
    (index: number) => {
      switch (index) {
        case 0:
          navigate(`${basePath}/${appName === 'app' ? 'pending-approval' : 'outstanding'}`)
          break
        case 1:
          navigate(`${basePath}/${appName === 'app' ? 'current' : 'paid'}`)
          break
        case 2:
          navigate(`${basePath}/sent`)
          break
        case 3:
          navigate(`${basePath}/paid`)
          break
        case 4:
          navigate(`${basePath}/settings`)
          break
        case 5:
          navigate(`${basePath}/archived`)
          break
        case 6:
          navigate(`${basePath}/deleted`)
          break
        default:
          navigate(basePath)
      }
    },
    [basePath, navigate, appName],
  )

  const { processResponse } = useContext(PopUpMessageContext)
  const goToInvoiceByNumber = useCallback(
    async (invoiceNumber: string) => {
      let asInt = parseInt(invoiceNumber, 10)
      if (Number.isNaN(asInt)) {
        processResponse({ error: 'Invalid invoice number' })
        return
      }

      if (asInt < 1000) {
        asInt += 1000
      }

      const invoiceDoc = await getDocs(
        query(collection(db, INVOICES), where('invoiceNumber', '==', asInt), limit(1)),
      )
      if (invoiceDoc.empty) {
        const deletedInvoiceDoc = await getDocs(
          query(collection(db, INVOICES_DELETED), where('invoiceNumber', '==', asInt), limit(1)),
        )
        if (deletedInvoiceDoc.empty) {
          processResponse({ error: 'Invoice not found' })
          return
        }
        const invoiceId = deletedInvoiceDoc.docs[0].id
        navigate(`${basePath}/deleted/${invoiceId}`)
        return
      }
      const invoiceId = invoiceDoc.docs[0].id
      const tab = !invoicesTab || invoicesTab === 'deleted' ? 'pending-approval' : invoicesTab
      navigate(`${basePath}/${tab}/${invoiceId}`)
    },
    [processResponse, basePath, navigate, invoicesTab],
  )

  return useMemo(
    () => ({
      selectedPracticeId,
      selectPractice: (id: string | null) => {
        setSelectedPracticeId(id)
      },
      basePath,
      createInvoiceOpen,
      setCreateInvoiceOpen,
      selectedInvoiceId,
      invoicesTab: invoicesTab || defaultTab,
      goToInvoiceByNumber,
      selectInvoice: (id: string | null) => {
        if (!id) navigate(`${basePath}/${invoicesTab ?? defaultTab}`)
        else navigate(`${basePath}/${invoicesTab ?? defaultTab}/${id}`)
      },
      tabIndex,
      deselectPregnancy: () => {
        if (selectedInvoiceId) {
          navigate(`${basePath}/${invoicesTab || defaultTab}/${selectedInvoiceId}`)
        } else navigate(basePath)
      },
      selectPregnancy: (invoiceId: string, assessmentId: string, tab?: string) =>
        navigate(
          `${basePath}/${invoicesTab || defaultTab}/${invoiceId}/pregnancies/${assessmentId}${tab ? `/${tab}` : ''}`,
        ),
      onTabSelect,
    }),
    [
      basePath,
      defaultTab,
      selectedInvoiceId,
      goToInvoiceByNumber,
      selectedPracticeId,
      navigate,
      onTabSelect,
      invoicesTab,
      tabIndex,
      createInvoiceOpen,
      setCreateInvoiceOpen,
    ],
  )
}

export const InvoicesViewProvider = ({ children }: PropsWithChildren) => {
  const data = useInvoicesViewData('/super-admin/invoices')
  return <InvoicesViewContext.Provider value={data}>{children}</InvoicesViewContext.Provider>
}
